// ============================================================
// pages/MenuPage.jsx — Connected to Firebase
// ============================================================

import React, { useState, useEffect } from "react";
import { useProducts } from "../hooks/useFirestore";
import { ReviewService } from "../services/firebaseServices";
import { useAuth } from "../contexts/AuthContext";

const PriceDisplay = ({ product, compact = false }) => {
  const fmt = (p) => `Rs ${p}`;
  if (product.pricingType === "per_size" && product.sizes?.length) {
    if (compact) return <span className="price-tag">From {fmt(Math.min(...product.sizes.map((s) => s.price)))}</span>;
    return <span className="price-tag">{fmt(product.sizes[0]?.price)} — {fmt(product.sizes[product.sizes.length - 1]?.price)}</span>;
  }
  if (product.pricingType === "range") return <span className="price-tag">{fmt(product.rangeMin)} — {fmt(product.rangeMax)}</span>;
  if (product.pricingType === "fixed") {
    if (product.promoPrice) {
      return (
        <span className="price-tag">
          <span style={{ textDecoration: "line-through", opacity: 0.5, marginRight: 6 }}>{fmt(product.fixedPrice)}</span>
          <span style={{ color: "#e74c3c" }}>{fmt(product.promoPrice)}</span>
        </span>
      );
    }
    return <span className="price-tag">{fmt(product.fixedPrice)}</span>;
  }
  return null;
};

export default function MenuPage({ showToast, setCurrentView, navigateToBook }) {
  const { products, loading } = useProducts();
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selSize, setSelSize] = useState(null);

  // Review state
  const [productReviews, setProductReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewHover, setReviewHover] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);

  // Load reviews when a product is selected
  useEffect(() => {
    if (selectedProduct) {
      setReviewsLoading(true);
      ReviewService.getByProduct(selectedProduct.id)
        .then(setProductReviews)
        .catch(() => setProductReviews([]))
        .finally(() => setReviewsLoading(false));
    } else {
      setProductReviews([]);
      setReviewRating(5);
      setReviewComment("");
    }
  }, [selectedProduct]);

  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      showToast("Please write a comment", "error");
      return;
    }
    setSubmittingReview(true);
    try {
      await ReviewService.create({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      showToast("Review submitted! Thank you!", "success");
      setReviewComment("");
      setReviewRating(5);
      // Refresh reviews
      const updated = await ReviewService.getByProduct(selectedProduct.id);
      setProductReviews(updated);
    } catch (err) {
      showToast(err.message || "Failed to submit review", "error");
    } finally {
      setSubmittingReview(false);
    }
  };

  const categories = ["all", ...new Set(products.map((p) => p.category))];
  const filtered = products
    .filter((p) => p.available)
    .filter((p) => categoryFilter === "all" || p.category === categoryFilter)
    .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Our Menu</h2>
          <p>Browse our full range of handcrafted goodies</p>
        </div>
      </div>

      <div className="filters">
        {categories.map((c) => (
          <button key={c}
            className={`filter-btn ${categoryFilter === c ? "active" : ""}`}
            onClick={() => setCategoryFilter(c)}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div className="search-wrap">
          <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "var(--cocoa)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input className="search-box" placeholder="Search..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading menu...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No products found</h3>
          <p>Try adjusting your filters</p>
        </div>
      ) : (
        <div className="product-grid">
          {filtered.map((p) => (
            <div key={p.id} className="product-card" onClick={() => { setSelectedProduct(p); setSelSize(p.sizes?.[0]?.label || null); }}>
              <div className="product-card-img-wrap">
                <img src={p.thumbnailURL || p.image} alt={p.name} className="product-card-img" />
                {p.promoPrice && <div className="product-card-badge">Sale</div>}
                {p.isNew && <div className="product-card-badge" style={{ background: "var(--sage)" }}>New</div>}
              </div>
              <div className="product-card-body">
                <div className="product-card-cat">{p.category}</div>
                <div className="product-card-name">{p.name}</div>
                <div className="product-card-desc">{p.description}</div>
                <div className="product-card-footer">
                  <PriceDisplay product={p} compact />
                  <div className="product-card-rating">
                    <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#f5a623", stroke: "#f5a623" }}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    {p.averageRating?.toFixed(1) || "—"}
                  </div>
                </div>
                <div className="tag-row" style={{ marginTop: "0.5rem" }}>
                  <span className="inline-tag">
                    {p.pricingType === "per_size" ? "Priced by size" : p.pricingType === "range" ? "Custom pricing" : "Fixed price"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProduct(null)}>✕</button>
            <img src={selectedProduct.thumbnailURL || selectedProduct.image} alt={selectedProduct.name} className="modal-img" />
            <div className="modal-body">
              <div className="product-card-cat">{selectedProduct.category}</div>
              <h2>{selectedProduct.name}</h2>
              <p style={{ color: "var(--cocoa)", lineHeight: 1.7, margin: "0.5rem 0 1rem" }}>
                {selectedProduct.description}
              </p>

              {selectedProduct.pricingType === "per_size" && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Select Size</div>
                  <div className="size-picker">
                    {selectedProduct.sizes.map((s) => (
                      <button key={s.label}
                        className={`size-btn ${selSize === s.label ? "selected" : ""}`}
                        onClick={() => setSelSize(s.label)}>
                        {s.label}
                        <span className="size-price">Rs {s.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedProduct.pricingType === "fixed" && (
                <div style={{ fontSize: "1.3rem", fontWeight: 700, margin: "1rem 0" }}>
                  <PriceDisplay product={selectedProduct} />
                </div>
              )}

              {selectedProduct.pricingType === "range" && (
                <div style={{ fontSize: "1.1rem", fontWeight: 600, margin: "1rem 0", color: "var(--chai-dark)" }}>
                  Rs {selectedProduct.rangeMin} — Rs {selectedProduct.rangeMax}
                  <div style={{ fontSize: "0.82rem", color: "var(--cocoa)", fontWeight: 400 }}>
                    Final price depends on customisation
                  </div>
                </div>
              )}

              {selectedProduct.preparationTime && (
                <div style={{ fontSize: "0.85rem", color: "var(--cocoa)", margin: "0.5rem 0" }}>
                  ⏱ Preparation: {selectedProduct.preparationTime}
                </div>
              )}

              {selectedProduct.allergens?.length > 0 && (
                <div style={{ marginTop: "0.75rem" }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 4 }}>Allergens</div>
                  <div className="tag-row">
                    {selectedProduct.allergens.map((a) => (
                      <span key={a} className="inline-tag">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedProduct.ingredients?.length > 0 && (
                <div style={{ marginTop: "0.75rem" }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 4 }}>Ingredients</div>
                  <div className="tag-row">
                    {selectedProduct.ingredients.map((a) => (
                      <span key={a} className="inline-tag" style={{ background: "rgba(156,175,136,0.15)", color: "var(--sage-dark)" }}>{a}</span>
                    ))}
                  </div>
                </div>
              )}

              <button className="btn btn-primary" style={{ marginTop: "1.5rem", width: "100%", justifyContent: "center" }}
                onClick={() => { setSelectedProduct(null); navigateToBook(selectedProduct, selSize); }}>
                📅 Book This Product
              </button>

              {/* Reviews Section */}
              <div style={{ marginTop: "2rem", borderTop: "1px solid rgba(196,136,58,0.1)", paddingTop: "1.5rem" }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", marginBottom: "1rem" }}>
                  Customer Reviews
                  {selectedProduct.averageRating > 0 && (
                    <span style={{ fontSize: "0.85rem", fontWeight: 400, color: "var(--cocoa)", marginLeft: 8 }}>
                      ⭐ {selectedProduct.averageRating?.toFixed(1)} ({selectedProduct.totalReviews || 0})
                    </span>
                  )}
                </h3>

                {/* Write a Review */}
                {isAuthenticated ? (
                  <div style={{
                    background: "var(--cream)", borderRadius: 10, padding: "1rem",
                    marginBottom: "1.25rem",
                  }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                      Rate this product
                    </div>
                    <div style={{ display: "flex", gap: 4, marginBottom: "0.75rem" }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} viewBox="0 0 24 24"
                          onClick={() => setReviewRating(star)}
                          onMouseEnter={() => setReviewHover(star)}
                          onMouseLeave={() => setReviewHover(0)}
                          style={{
                            width: 28, height: 28, cursor: "pointer", transition: "transform 0.15s",
                            fill: star <= (reviewHover || reviewRating) ? "#f5a623" : "#ddd",
                            stroke: star <= (reviewHover || reviewRating) ? "#f5a623" : "#ccc",
                            transform: star <= (reviewHover || reviewRating) ? "scale(1.1)" : "scale(1)",
                          }}>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ))}
                      <span style={{ marginLeft: 8, fontSize: "0.85rem", color: "var(--cocoa)", alignSelf: "center" }}>
                        {reviewRating === 1 ? "Poor" : reviewRating === 2 ? "Fair" : reviewRating === 3 ? "Good" : reviewRating === 4 ? "Very Good" : "Excellent"}
                      </span>
                    </div>
                    <textarea className="form-textarea" rows="2" value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Share your experience with this product..."
                      style={{ marginBottom: "0.75rem" }} />
                    <button className="btn btn-primary btn-sm" onClick={handleSubmitReview}
                      disabled={submittingReview}>
                      {submittingReview ? "Submitting..." : "Submit Review"}
                    </button>
                  </div>
                ) : (
                  <div style={{
                    background: "var(--cream)", borderRadius: 10, padding: "1rem",
                    marginBottom: "1.25rem", textAlign: "center", fontSize: "0.88rem", color: "var(--cocoa)",
                  }}>
                    Sign in to leave a review
                  </div>
                )}

                {/* Reviews List */}
                {reviewsLoading ? (
                  <div style={{ textAlign: "center", color: "var(--cocoa)", fontSize: "0.85rem", padding: "1rem 0" }}>
                    Loading reviews...
                  </div>
                ) : productReviews.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--cocoa)", fontSize: "0.85rem", padding: "1rem 0" }}>
                    No reviews yet. Be the first to review!
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {productReviews.map((r) => (
                      <div key={r.id} style={{
                        padding: "0.75rem", borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.04)", background: "rgba(255,255,255,0.6)",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <strong style={{ fontSize: "0.85rem" }}>{r.customerName}</strong>
                            <div style={{ display: "flex", gap: 1 }}>
                              {Array.from({ length: r.rating }).map((_, i) => (
                                <svg key={i} viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: "#f5a623", stroke: "#f5a623" }}>
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                              ))}
                            </div>
                          </div>
                          <span style={{ fontSize: "0.72rem", color: "var(--cocoa)" }}>
                            {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                          </span>
                        </div>
                        <p style={{ fontSize: "0.85rem", color: "var(--cocoa)", lineHeight: 1.5, margin: 0 }}>
                          {r.comment}
                        </p>
                        {r.adminReply && (
                          <div style={{
                            marginTop: "0.5rem", padding: "0.5rem 0.75rem", borderRadius: 6,
                            background: "rgba(196,136,58,0.06)", borderLeft: "3px solid var(--chai)",
                          }}>
                            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--chai)", marginBottom: 2 }}>
                              Chaitimecrunch replied
                            </div>
                            <p style={{ fontSize: "0.82rem", color: "var(--cocoa)", margin: 0, lineHeight: 1.4 }}>
                              {r.adminReply}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}